// admin-panel/src/components/AdminRightPane.js

import {
  Box,
  Flex,
  IconButton,
  Text,
  Button,
  VStack,
  Badge,
  Tooltip,
  HStack,
  Divider,
} from "@chakra-ui/react";
// import { CloseIcon } from "@chakra-ui/icons";
import { FiX } from "react-icons/fi";
import { Download, CheckCircle2, Circle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import MarkdownRenderer from './MarkdownRenderer';

export default function AdminRightPane({
  activeTurn,
  onClose,
  onScroll,
  rightPaneRef,
  content,
  extractedSuggestions,
  selectedSuggestions,
  turnId,
}) {
  const API_BASE_URL = process.env.REACT_APP_SERVER_API_URL || 'http://localhost:8080';

  const isSuggestionsView = content?.type === 'suggestions';
  const isReportView = content?.type === 'report';

  // Tab state for both views
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Get feedback from content
  const feedback = content?.feedback || null;
  const isThumbsUpActive = feedback?.type === 'positive';
  const isThumbsDownActive = feedback?.type === 'negative';

  // Reset tab to 0 whenever content type changes
  useEffect(() => {
    setActiveTabIndex(0);
  }, [content?.type]);


  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pdf/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          markdown: content?.content || '',
          title: 'Alpherra Report - Admin View',
          version: content?.version || 1
        })
      });

      if (!response.ok) throw new Error('PDF generation failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Admin_Report_v${content?.version || 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const allSuggestions = extractedSuggestions || {};
  const selectedKeys = content?.type === 'report' && content?.selectedSuggestions
    ? content.selectedSuggestions
    : (selectedSuggestions || []);
  const suggestionEntries = Object.entries(allSuggestions);

  // Format timestamp for display
  const formatFeedbackTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <Flex
      direction="column"
      h="100%"
      w="100%"
      bg="white"
      border="1px solid"
      borderColor="#ECECEC"
      boxSizing="border-box"
      borderRadius="12px"
      m="2"
    >
      {/* HEADER */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
        py="12px"
        pr="16px"
        pl="16px"
        borderBottom="1px solid"
        borderColor="#E0E0E0"
      >
        {/* LEFT: Tabs for Suggestions (2 tabs: Results, Feedback) */}
        {isSuggestionsView && (
          <Flex gap="8px" align="center">
            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              color={activeTabIndex === 0 ? '#CF008A' : '#666'}
              bg="transparent"
              px="16px"
              py="8px"
              borderRadius="8px"
              position="relative"
              onClick={() => setActiveTabIndex(0)}
              _hover={{
                color: '#CF008A',
                bg: 'rgba(207, 0, 138, 0.05)'
              }}
              _after={activeTabIndex === 0 ? {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #CF008A 0%, #037A9B 100%)',
                borderRadius: '2px 2px 0 0'
              } : {}}
            >
              Results
            </Button>

            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              color={activeTabIndex === 1 ? '#037A9B' : '#666'}
              bg="transparent"
              px="16px"
              py="8px"
              borderRadius="8px"
              position="relative"
              onClick={() => setActiveTabIndex(1)}
              _hover={{
                color: '#037A9B',
                bg: 'rgba(3, 122, 155, 0.05)'
              }}
              _after={activeTabIndex === 1 ? {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #037A9B 0%, #00C2E5 100%)',
                borderRadius: '2px 2px 0 0'
              } : {}}
            >
              Feedback
            </Button>
          </Flex>
        )}

        {/* LEFT: Tabs for Report (3 tabs: Report, Selections, Feedback) */}
        {isReportView && (
          <Flex gap="8px" align="center">
            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              color={activeTabIndex === 0 ? '#CF008A' : '#666'}
              bg="transparent"
              px="16px"
              py="8px"
              borderRadius="8px"
              position="relative"
              onClick={() => setActiveTabIndex(0)}
              _hover={{
                color: '#CF008A',
                bg: 'rgba(207, 0, 138, 0.05)'
              }}
              _after={activeTabIndex === 0 ? {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #CF008A 0%, #037A9B 100%)',
                borderRadius: '2px 2px 0 0'
              } : {}}
            >
              Report
            </Button>

            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              color={activeTabIndex === 1 ? '#037A9B' : '#666'}
              bg="transparent"
              px="16px"
              py="8px"
              borderRadius="8px"
              position="relative"
              onClick={() => setActiveTabIndex(1)}
              _hover={{
                color: '#037A9B',
                bg: 'rgba(3, 122, 155, 0.05)'
              }}
              _after={activeTabIndex === 1 ? {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #037A9B 0%, #00C2E5 100%)',
                borderRadius: '2px 2px 0 0'
              } : {}}
            >
              Selections {selectedKeys.length > 0 && `(${selectedKeys.length})`}
            </Button>

            <Button
              size="sm"
              fontSize="16px"
              fontWeight="600"
              color={activeTabIndex === 2 ? '#037A9B' : '#666'}
              bg="transparent"
              px="16px"
              py="8px"
              borderRadius="8px"
              position="relative"
              onClick={() => setActiveTabIndex(2)}
              _hover={{
                color: '#037A9B',
                bg: 'rgba(3, 122, 155, 0.05)'
              }}
              _after={activeTabIndex === 2 ? {
                content: '""',
                position: 'absolute',
                bottom: '-12px',
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #037A9B 0%, #00C2E5 100%)',
                borderRadius: '2px 2px 0 0'
              } : {}}
            >
              Feedback
            </Button>
          </Flex>
        )}

        {/* RIGHT: Action Buttons */}
        <Flex gap="12px" alignItems="center" ml="auto">
          {/* Feedback Icons - Read Only Display (Quick View) */}
          <Tooltip
            label={
              feedback
                ? `${feedback.type === 'positive' ? 'Positive' : 'Negative'} Feedback${feedback.comment ? ': View details in Feedback tab' : ''}`
                : 'No feedback'
            }
            fontSize="sm"
          >
            <Flex gap="8px" alignItems="center">
              <IconButton
                icon={<ThumbsUp size={18} />}
                minW="unset"
                h="32px"
                w="32px"
                p={0}
                bg={isThumbsUpActive ? "#E8F5E9" : "transparent"}
                color={isThumbsUpActive ? "#4CAF50" : "#CCCCCC"}
                aria-label="Thumbs up"
                variant="ghost"
                isDisabled={true}
                cursor="default"
                _hover={{ bg: isThumbsUpActive ? "#E8F5E9" : "transparent" }}
              />

              <IconButton
                icon={<ThumbsDown size={18} />}
                minW="unset"
                h="32px"
                w="32px"
                p={0}
                bg={isThumbsDownActive ? "#FFEBEE" : "transparent"}
                color={isThumbsDownActive ? "#F44336" : "#CCCCCC"}
                aria-label="Thumbs down"
                variant="ghost"
                isDisabled={true}
                cursor="default"
                _hover={{ bg: isThumbsDownActive ? "#FFEBEE" : "transparent" }}
              />
            </Flex>
          </Tooltip>

          {/* Separator */}
          <Box w="1px" h="24px" bg="#E0E0E0" mx="1" />

          {/* Download Report Button - Only when Report tab is active */}
          {/* {isReportView && activeTabIndex === 0 && (
            <Button
              leftIcon={<Download size={16} />}
              size="sm"
              px="14px"
              onClick={handleDownloadPDF}
              bg="#CF008A"
              color="#FFFFFF"
              borderRadius="12px"
              _hover={{ bg: "#D933A1" }}
              _active={{ bg: "#CF008A" }}
            >
              Download Report
            </Button>
          )} */}

          <IconButton
            size="sm"
            icon={<FiX w="12px" h="12px" />}
            variant="ghost"
            onClick={onClose}
            aria-label="Close suggestions"
          />
        </Flex>
      </Flex>

      {/* CONTENT */}
      <Box
        position="relative"
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        p="16px"
        fontSize="14px"
        css={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {/* Suggestions View - Content based on active tab */}
        {isSuggestionsView && (
          <Box>
            {/* Tab 0: Results Content */}
            {activeTabIndex === 0 && (
              <Box
                ref={rightPaneRef} onScroll={onScroll}>
                <MarkdownRenderer content={content.content} />
              </Box>
            )}

            {/* Tab 1: Feedback Details */}
            {activeTabIndex === 1 && (
              <Box>
                {!feedback || (!feedback.type && !feedback.comment) ? (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    h="400px"
                    color="#999"
                  >
                    <Text fontSize="16px" fontWeight="500">
                      No feedback available for these suggestions
                    </Text>
                  </Flex>
                ) : (
                  <VStack spacing="16px" align="stretch" w="100%" mt="8px" maxW="600px" mx="auto" >
                    {/* Feedback Type Card */}
                    <Box
                      p="16px"
                      bg={feedback.type === 'positive' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)'}
                      borderRadius="12px"
                      border="2px solid"
                      borderColor={feedback.type === 'positive' ? '#4CAF50' : '#F44336'}
                    >
                      <Flex align="center" gap="10px" mb="2">
                        {feedback.type === 'positive' ? (
                          <ThumbsUp size={20} color="#4CAF50" strokeWidth={2.5} />
                        ) : (
                          <ThumbsDown size={20} color="#F44336" strokeWidth={2.5} />
                        )}
                        <Text fontSize="14px" fontWeight="700" color={feedback.type === 'positive' ? '#4CAF50' : '#F44336'}>
                          {feedback.type === 'positive' ? 'Positive Feedback' : 'Negative Feedback'}
                        </Text>
                      </Flex>

                      {feedback.comment && (
                        <Box mt="12px" pt="12px" borderTop="1px solid" borderColor={feedback.type === 'positive' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'}>
                          <Text fontSize="16px" color="#333" lineHeight="1.7" fontStyle="italic">
                            "{feedback.comment}"
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </VStack>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Report View - Content based on active tab */}
        {isReportView && (
          <Box>
            {/* Tab 0: Report Content */}
            {activeTabIndex === 0 && (
              <Box ref={rightPaneRef} onScroll={onScroll}>
                <MarkdownRenderer content={content.content} />
              </Box>
            )}

            {/* Tab 1: Selected Optimizations */}
            {activeTabIndex === 1 && (
              <>
                {selectedKeys.length === 0 ? (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    h="400px"
                    color="#999"
                  >
                    <Text fontSize="16px" fontWeight="500">
                      No optimization suggestions were selected
                    </Text>
                  </Flex>
                ) : (
                  <VStack spacing="16px" align="stretch" w="100%">
                    {/* Header Summary */}
                    {selectedKeys.length > 0 && (
                      <Box
                        mt="8px"
                        p="14px"
                        bg="rgba(3, 122, 155, 0.05)"
                        borderRadius="10px"
                        border="1px solid rgba(3, 122, 155, 0.15)"
                      >
                        <Flex align="center" gap="8px">
                          <CheckCircle2 size={18} color="#037A9B" />
                          <Text fontSize="13px" color="#037A9B" fontWeight="600">
                            {selectedKeys.length} of {suggestionEntries.length} optimizations included in this report
                          </Text>
                        </Flex>
                      </Box>
                    )}

                    {/* Suggestions List */}
                    <VStack spacing="10px" align="stretch">
                      {suggestionEntries.map(([key, suggestion], index) => {
                        const isSelected = selectedKeys.includes(key);

                        const title = typeof suggestion === 'object' ? suggestion.title :
                          key.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ');
                        const description = typeof suggestion === 'object' ? suggestion.description : suggestion;

                        return (
                          <Box
                            key={key}
                            p="16px"
                            borderRadius="12px"
                            bg={isSelected ? 'rgba(3, 122, 155, 0.04)' : '#FAFAFA'}
                            border="2px solid"
                            borderColor={isSelected ? '#037A9B' : '#E8E8E8'}
                            transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
                            position="relative"
                            overflow="hidden"
                          >
                            {/* Left accent bar for selected items */}
                            {isSelected && (
                              <Box
                                position="absolute"
                                left={0}
                                top={0}
                                bottom={0}
                                w="4px"
                                bg="linear-gradient(180deg, #037A9B 0%, #00C2E5 100%)"
                              />
                            )}

                            <Flex align="flex-start" gap="14px">
                              {/* Icon */}
                              <Box flexShrink={0} mt="2px" p="4px" borderRadius="8px" bg={isSelected ? 'rgba(3, 122, 155, 0.1)' : 'transparent'} transition="all 0.2s">
                                {isSelected ? (
                                  <CheckCircle2 size={22} color="#037A9B" strokeWidth={2.5} />
                                ) : (
                                  <Circle size={22} color="#BEBEBE" strokeWidth={2} />
                                )}
                              </Box>

                              <Flex direction="column" flex={1} gap="8px">
                                <Flex align="center" gap="10px">
                                  <Badge
                                    fontSize="11px"
                                    fontWeight="700"
                                    px="8px"
                                    py="2px"
                                    borderRadius="6px"
                                    bg={isSelected ? '#037A9B' : '#D0D0D0'}
                                    color="white"
                                    textTransform="uppercase"
                                    letterSpacing="0.5px"
                                  >
                                    {String(index + 1).padStart(2, '0')}
                                  </Badge>
                                  <Text
                                    fontSize="15px"
                                    fontWeight="700"
                                    color={isSelected ? '#037A9B' : '#242424'}
                                    lineHeight="1.4"
                                  >
                                    {title}
                                  </Text>
                                </Flex>

                                <Text fontSize="14px" color="#555" lineHeight="1.7" pl="2px">
                                  {description}
                                </Text>

                                {isSelected && (
                                  <Flex align="center" gap="6px" mt="4px">
                                    <Box w="8px" h="8px" borderRadius="50%" bg="#037A9B" boxShadow="0 0 0 3px rgba(3, 122, 155, 0.2)" />
                                    <Text fontSize="12px" fontWeight="600" color="#037A9B" textTransform="uppercase" letterSpacing="0.5px">
                                      Included in Report
                                    </Text>
                                  </Flex>
                                )}
                              </Flex>
                            </Flex>
                          </Box>
                        );
                      })}
                    </VStack>
                  </VStack>
                )}
              </>
            )}

            {/* Tab 2: Feedback Details */}
            {activeTabIndex === 2 && (
              <Box>
                {!feedback || (!feedback.type && !feedback.comment) ? (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    h="400px"
                    color="#999"
                  >
                    <Text fontSize="16px" fontWeight="500">
                      No feedback available for this report
                    </Text>
                  </Flex>
                ) : (
                  <VStack spacing="16px" align="stretch" w="100%" mt="8px" maxW="600px" mx="auto" >
                    {/* Feedback Type Card */}
                    <Box
                      p="16px"
                      bg={feedback.type === 'positive' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)'}
                      borderRadius="12px"
                      border="2px solid"
                      borderColor={feedback.type === 'positive' ? '#4CAF50' : '#F44336'}
                    >
                      <Flex align="center" gap="10px" mb="2">
                        {feedback.type === 'positive' ? (
                          <ThumbsUp size={20} color="#4CAF50" strokeWidth={2.5} />
                        ) : (
                          <ThumbsDown size={20} color="#F44336" strokeWidth={2.5} />
                        )}
                        <Text fontSize="16px" fontWeight="700" color={feedback.type === 'positive' ? '#4CAF50' : '#F44336'}>
                          {feedback.type === 'positive' ? 'Positive Feedback' : 'Negative Feedback'}
                        </Text>
                      </Flex>

                      {feedback.comment && (
                        <Box mt="12px" pt="12px" borderTop="1px solid" borderColor={feedback.type === 'positive' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'}>
                          <Text fontSize="16px" color="#333" lineHeight="1.7" fontStyle="italic">
                            "{feedback.comment}"
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </VStack>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Footer - Selection Summary (only show for Selections tab in Report view) */}
      {isReportView && activeTabIndex === 1 && selectedKeys.length > 0 && (
        <Flex
          alignItems="center"
          gap="8px"
          p="14px"
          bg="rgba(3, 122, 155, 0.05)"
          borderTop="1px solid"
          borderColor="rgba(3, 122, 155, 0.15)"
        >
          <CheckCircle2 size={18} color="#037A9B" />
          <Text fontSize="13px" color="#037A9B" fontWeight="600">
            {selectedKeys.length} of {suggestionEntries.length} optimizations included in this report
          </Text>
        </Flex>
      )}
    </Flex>
  );
}
